import styles from './manualCreditNoteForm.module.css'
import { useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { BooleanField, Column, Data, FieldType, FieldValue, Form, FormField, Hint, O, Option, option, PriceField, RadioField, StaticValue, Table } from "../../../../../features/ui"
import { useDataTypesHooks } from "../../../../../features/data-types"
import { TableName } from "../../../../../tables"
import { operationHelpers } from "../../../../operations"
import { Product } from "../../../../products"
import {
    correctManualAdjustmentPrices,
    getManualAdjustmentAmount,
    getManualCreditNoteMaxAmount,
    ManualCreditNoteData,
    ManualCreditNoteMode,
    prorateManualAdjustment,
    splitManualAdjustment,
    splitManualCreditNote
} from "../../../../administration"
import { HoardOrder, HoardType, SaleOperation } from "../../../state/types"
import { round } from "../../../../../utils/typeUtils"
import { useActions } from "./actions"

type Props = {
    table: TableName
    operation: SaleOperation
    data: ManualCreditNoteData
    products: Product[]
}

export const ManualCreditNoteForm = (props: Props) => {
    const { table, operation, data, products } = props

    const [mode, setMode] = useState<ManualCreditNoteMode>(ManualCreditNoteMode.PRORATED)
    const [amount, setAmount] = useState<O<number>>()
    const [correctPrices, setCorrectPrices] = useState<Record<string, number>>({})
    const [emitNow, setEmitNow] = useState(true)

    const { describeNoun, describeCode } = operationHelpers
    const { formatPrice } = useDataTypesHooks()
    const navigate = useNavigate()

    const { submitCreditNote } = useActions()

    // A financial hoard has no items when the note is created: the amount is prorated over its value
    const isFinancialHoard = table === TableName.HOARD_ORDERS && (operation as HoardOrder).type === HoardType.FINANCIAL
    const isProductsMode = !isFinancialHoard && mode === ManualCreditNoteMode.PRODUCTS

    const lines = useMemo(() => data.lines.filter(line => line.activeQuantity > 0 && line.unitPrice > 0), [data])

    // The notes can't credit more than what is left of the operation (without perceptions: a credit note never credits them)
    const maxAmount = useMemo(() => isFinancialHoard ?
        round(data.balances.creditableAmount + data.balances.remainingAmount) :
        getManualCreditNoteMaxAmount(lines, data.surchargeFactor)
    , [data, lines, isFinancialHoard])

    const correctedParts = useMemo(() =>
        correctManualAdjustmentPrices(new Map(Object.entries(correctPrices)), lines, data.surchargeFactor)
    , [correctPrices, lines, data])

    // What would be created: the fiscal part on what is invoiced and the internal one on what is left to invoice
    const split = useMemo(() => {
        if (isFinancialHoard) {
            return amount && amount > 0 ? splitManualCreditNote(Math.min(amount, maxAmount), data.balances) : undefined
        }
        const parts = isProductsMode ? correctedParts : prorateManualAdjustment(Math.min(amount || 0, maxAmount), lines, data.surchargeFactor)
        const { fiscal, internal } = splitManualAdjustment(parts, lines)
        return { fiscalAmount: getManualAdjustmentAmount(fiscal), internalAmount: getManualAdjustmentAmount(internal) }
    }, [data, lines, amount, correctedParts, isProductsMode, isFinancialHoard, maxAmount])

    const totalAmount = split ? round(split.fiscalAmount + split.internalAmount) : 0

    const describeSplit = () => {
        if (!split || totalAmount <= 0) return '-'
        const parts = [
            ...(split.fiscalAmount > 0 ? [`${formatPrice(split.fiscalAmount)} en NC fiscal (lo ya facturado)`] : []),
            ...(split.internalAmount > 0 ? [`${formatPrice(split.internalAmount)} a descontar de lo que falta facturar`] : [])
        ]
        return parts.join(' y ')
    }

    const findProductName = (itemId: string) => {
        const item = operation.items.find(item => item.id === itemId)
        return products.find(product => product.id === item?.productId)?.name || ''
    }

    const onChangeCorrectPrice = (itemId: string) => (value?: number) => {
        const { [itemId]: _, ...otherPrices } = correctPrices
        setCorrectPrices(value === undefined ? otherPrices : { ...otherPrices, [itemId]: value })
    }

    const productColumns: Column[] = [
        {
            name: 'itemId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => findProductName(value as string)
        },
        {
            name: 'activeQuantity',
            type: FieldType.NUMBER,
            label: 'Cantidad'
        },
        {
            name: 'unitPrice',
            type: FieldType.PRICE,
            label: 'Precio Actual',
            render: (value?: FieldValue) => <StaticValue type={FieldType.PRICE} value={value} />
        },
        {
            name: 'correctPrice',
            type: FieldType.PRICE,
            label: 'Precio Correcto',
            render: (value?: FieldValue) => {
                const itemId = value as string
                const line = lines.find(line => line.itemId === itemId)!
                return <div className={styles.correctPrice}>
                    <PriceField
                        id={`correct-price-${itemId}`}
                        value={correctPrices[itemId]}
                        min={0}
                        max={line.unitPrice}
                        optional
                        onChange={onChangeCorrectPrice(itemId)}
                    />
                </div>
            }
        },
        {
            name: 'creditAmount',
            type: FieldType.PRICE,
            label: 'Nota de Crédito',
            render: (value?: FieldValue) => {
                const part = correctedParts.find(part => part.itemId === value)
                return part ? <StaticValue type={FieldType.PRICE} value={getManualAdjustmentAmount([part])} /> : '-'
            }
        }
    ]

    const productRows = lines.map(line => ({ ...line, correctPrice: line.itemId, creditAmount: line.itemId }))

    const modeOptions = [
        option(ManualCreditNoteMode.PRORATED, 'Prorrateado'),
        option(ManualCreditNoteMode.PRODUCTS, 'Sobre productos')
    ]
    const modeHints: Record<string, string> = {
        [ManualCreditNoteMode.PRORATED]: 'Un monto repartido entre todos los productos.',
        [ManualCreditNoteMode.PRODUCTS]: 'Corregir el precio de algunos productos.'
    }

    const renderModeLabel = (modeOption: Option) => (<>
        {modeOption.label}
        <Hint text={modeHints[modeOption.value]} className={styles.modeHint} />
    </>)

    const fields: FormField[] = [
        {
            name: 'operation',
            type: FieldType.TEXT,
            label: describeNoun(table),
            render: () => <>{describeCode(operation)}</>
        },
        ...(!isFinancialHoard ? [{
            name: 'mode',
            type: FieldType.TEXT,
            label: 'Aplicar',
            render: () => <RadioField
                id="manual-credit-note-mode"
                value={mode}
                options={modeOptions}
                renderLabel={renderModeLabel}
                onChange={value => value && setMode(value as ManualCreditNoteMode)}
            />
        }] : []),
        ...(isProductsMode ? [{
            name: 'products',
            type: FieldType.TABLE,
            label: 'Productos',
            hint: 'Precio unitario con IVA y descuentos, sin el recargo del medio de pago. Completá solo los que hay que corregir.',
            render: () => <Table
                columns={productColumns}
                rows={productRows as unknown as Data[]}
                rowId="itemId"
                alignRight={false}
                readOnly
            />
        }] : [
            {
                name: 'maxAmount',
                type: FieldType.PRICE,
                label: 'Monto Disponible',
                render: () => <>{formatPrice(maxAmount)}</>
            },
            {
                name: 'amount',
                type: FieldType.PRICE,
                label: 'Monto',
                max: maxAmount,
                focus: true,
                onChange: (value?: FieldValue) => setAmount(value as O<number>),
                validate: (formData: Data) => {
                    const formAmount = (formData.amount as number) || 0
                    if (formAmount <= 0) return 'El monto debe ser mayor a $0,00'
                    if (formAmount > maxAmount) return `El monto no puede superar ${formatPrice(maxAmount)}`
                    return null
                }
            }
        ]),
        {
            name: 'result',
            type: FieldType.TEXT,
            label: 'Se genera',
            hint: 'Si la operación ya está pagada, el monto queda como crédito a favor.',
            render: () => <>{describeSplit()}</>
        },
        ...(split && split.fiscalAmount > 0 ? [{
            name: 'emitNow',
            type: FieldType.BOOLEAN,
            label: 'Emitir a ARCA',
            render: () => <BooleanField
                id="emit-manual-credit-note"
                label={`Emitir la NC fiscal por ${formatPrice(split.fiscalAmount)} a ARCA ahora`}
                value={emitNow}
                optional
                onChange={value => setEmitNow(!!value)}
                hint="Si no, queda pendiente para emitir desde Documentos."
            />
        }] : []),
        {
            name: 'reason',
            type: FieldType.LONG_TEXT,
            label: 'Motivo',
            narrow: true,
            placeholder: 'Ej: Faltó aplicar el descuento por pago contado',
            validate: (formData: Data) => (formData.reason as string)?.trim() ? null : 'Debe indicar el motivo'
        }
    ]

    // Repeated clicks or Enter before the spinner shows up would create the same note several times (with the same code)
    const submittingRef = useRef(false)
    const [submitting, setSubmitting] = useState(false)

    const onSubmit = async (formData: Data) => {
        if (submittingRef.current) return
        submittingRef.current = true
        setSubmitting(true)
        try {
            const request = isProductsMode ?
                { mode: ManualCreditNoteMode.PRODUCTS, correctPrices } :
                { mode: ManualCreditNoteMode.PRORATED, amount: formData.amount as number }
            await submitCreditNote(table, operation.id, request, (formData.reason as string).trim(), emitNow)
        } finally {
            submittingRef.current = false
            setSubmitting(false)
        }
    }

    const onCancel = () => navigate(-1)

    return (
        <Form
            formId="manual-credit-note-form"
            fields={fields}
            createLabel="Confirmar"
            disableSubmit={submitting || totalAmount <= 0}
            onSubmit={onSubmit}
            onCancel={onCancel}
        />
    )
}
